import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, ShieldCheck, Building2, SlidersHorizontal, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useProducts, slug } from "@/lib/products";
import { PATHWAYS } from "@/components/Pathways";
import ProductCard from "@/components/ProductCard";
import CoaModal from "@/components/CoaModal";
import { Input } from "@/components/ui/input";

const SORTS = [
  ["name", "Alphabetical"],
  ["price-asc", "Price · low to high"],
  ["price-desc", "Price · high to low"],
  ["purity", "Highest purity"],
];

const Toggle = ({ on, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid} aria-pressed={on}
    className={`w-full flex items-center justify-between font-tech text-[11px] uppercase tracking-wide px-3 py-2 border rounded-full transition-colors ${
      on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"}`}>
    {children}<span className={`h-1.5 w-1.5 rounded-full ${on ? "bg-emerald-400" : "bg-slate-300"}`} />
  </button>
);

export default function CatalogPage() {
  const products = useProducts();
  const [params, setParams] = useSearchParams();
  const cat = params.get("cat") || "All";
  const query = params.get("q") || "";
  const [sort, setSort] = useState("name");
  const [coaOnly, setCoaOnly] = useState(false);
  const [stockOnly, setStockOnly] = useState(false);
  const [coa, setCoa] = useState(null);
  const [railOpen, setRailOpen] = useState(false);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    v && v !== "All" ? next.set(k, v) : next.delete(k);
    setParams(next, { replace: true });
  };

  const counts = useMemo(() => products.reduce((m, p) => ((m[p.category] = (m[p.category] || 0) + 1), m), {}), [products]);
  const categories = ["All", ...PATHWAYS.map((p) => p.cat).filter((c) => counts[c])];

  const filtered = products
    .filter((p) => (cat === "All" || p.category === cat) &&
      (!coaOnly || p.has_coa_file) && (!stockOnly || !p.turnaround) &&
      `${p.name} ${p.alias || ""} ${p.sku}`.toLowerCase().includes(query.toLowerCase()))
    .sort((a, b) => sort === "price-asc" ? a.retail_price - b.retail_price
      : sort === "price-desc" ? b.retail_price - a.retail_price
      : sort === "purity" ? (b.coa_purity || 0) - (a.coa_purity || 0)
      : a.name.localeCompare(b.name) || a.retail_price - b.retail_price);

  const rail = (
    <div className="space-y-6">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input value={query} onChange={(e) => setParam("q", e.target.value)} placeholder="Search compounds or SKU..."
          data-testid="catalog-search-input" className="pl-9 rounded-sm bg-white" />
      </div>
      <div>
        <p className="font-tech text-[10px] uppercase tracking-[0.18em] text-slate-400 mb-2">Pathway</p>
        <div className="border-l border-slate-200">
          {categories.map((c) => (
            <button key={c} onClick={() => { setParam("cat", c); setRailOpen(false); }}
              data-testid={c === "All" ? "catalog-category-tab-all" : `catalog-category-tab-${slug(c)}`}
              className={`relative w-full text-left flex items-center justify-between pl-4 pr-2 py-2 text-sm transition-colors ${
                cat === c ? "text-slate-900 font-medium" : "text-slate-500 hover:text-slate-900"}`}>
              {cat === c && <motion.span layoutId="rail-indicator" className="absolute -left-px top-1 bottom-1 w-0.5 bg-sky-700" />}
              {c}
              <span className="font-tech text-[10px] text-slate-400">{c === "All" ? products.length : counts[c]}</span>
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="font-tech text-[10px] uppercase tracking-[0.18em] text-slate-400 mb-2">Refine</p>
        <div className="space-y-2">
          <Toggle on={coaOnly} onClick={() => setCoaOnly(!coaOnly)} testid="catalog-filter-coa-toggle">COA PDF on file</Toggle>
          <Toggle on={stockOnly} onClick={() => setStockOnly(!stockOnly)} testid="catalog-filter-stock-toggle">In stock now</Toggle>
        </div>
      </div>
      <div className="border-t border-slate-200 pt-4 space-y-2">
        <Link to="/coa-library" data-testid="catalog-rail-coa-link" className="flex items-center gap-2 text-sm text-slate-600 hover:text-sky-700 transition-colors">
          <ShieldCheck className="h-4 w-4" /> COA Library
        </Link>
        <a href="/#wholesale" data-testid="catalog-rail-wholesale-link" className="flex items-center gap-2 text-sm text-slate-600 hover:text-sky-700 transition-colors">
          <Building2 className="h-4 w-4" /> Wholesale tiers
        </a>
      </div>
    </div>
  );

  return (
    <main data-testid="catalog-page">
      <section className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <p className="font-tech text-xs uppercase tracking-[0.18em] text-sky-700 mb-2">Analytical Reference Standards</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900">Research Catalog</h1>
          <p className="mt-3 text-base text-slate-500 max-w-2xl">
            Manufactured by Axis Bio Lab · distributed through Arias Peptides. {products.length || ""} SKUs across {categories.length - 1} research pathways. Research use only.
          </p>
        </div>
      </section>
      <section id="catalog" className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12 grid lg:grid-cols-[250px_1fr] gap-8 lg:gap-12" data-testid="catalog-section">
        <aside className="hidden lg:block">
          <div className="sticky top-24" data-testid="catalog-sticky-rail">{rail}</div>
        </aside>
        <div>
          <div className="flex items-center justify-between gap-3 mb-5 pb-4 border-b border-slate-200">
            <p className="font-tech text-xs text-slate-500" data-testid="catalog-result-count">
              <span className="text-slate-900 font-semibold">{filtered.length}</span> compounds · {cat}{query ? ` · "${query}"` : ""}
            </p>
            <div className="flex items-center gap-2">
              <button onClick={() => setRailOpen(true)} data-testid="catalog-mobile-filter-btn"
                className="lg:hidden inline-flex items-center gap-1.5 font-tech text-[11px] uppercase tracking-wide border border-slate-300 bg-white px-3 py-2 rounded-sm">
                <SlidersHorizontal className="h-3.5 w-3.5" /> Filter
              </button>
              <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="catalog-sort-select"
                className="font-tech text-[11px] uppercase tracking-wide border border-slate-300 bg-white px-3 py-2 rounded-sm text-slate-700">
                {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
          <motion.div layout className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
            <AnimatePresence mode="popLayout">
              {filtered.map((p) => (
                <motion.div key={p.id} layout initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.98 }} transition={{ duration: 0.25 }}>
                  <ProductCard p={p} onCoa={setCoa} />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
          {filtered.length === 0 && products.length > 0 && (
            <p className="text-center text-slate-500 py-16 font-tech text-sm" data-testid="catalog-empty">No compounds match your filters.</p>
          )}
        </div>
      </section>
      <AnimatePresence>
        {railOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[60] bg-slate-900/40 lg:hidden" onClick={() => setRailOpen(false)}>
            <motion.div initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }} transition={{ type: "tween", duration: 0.25 }}
              onClick={(e) => e.stopPropagation()} data-testid="catalog-mobile-rail" className="h-full w-[85%] max-w-sm bg-white p-5 overflow-y-auto">
              <div className="flex items-center justify-between mb-5">
                <p className="font-display font-semibold text-slate-900">Filter catalog</p>
                <button onClick={() => setRailOpen(false)} data-testid="catalog-mobile-rail-close" aria-label="Close"><X className="h-5 w-5" /></button>
              </div>
              {rail}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <CoaModal product={coa} onClose={() => setCoa(null)} />
    </main>
  );
}
